import {
	ClinicalValueAlertThresholds,
	SystemInputAlert,
} from '../model/inputalert'
import { ContactLenseFormValues } from '../components/LensesTrial/types'
import { ContactLensesHistory } from '../model/exam'
import {
	LensDataWithAccuracy,
	PrescriptionContactLensesSingleEye,
} from '../model/prescription'
import {
	AutorefractionData,
	PhoropterData,
	TonometerData,
} from '../model/instruments'
import { RetinoscopyData } from '../components/Retinoscopy/models'

import { FieldsData, Path } from '../formFields/model'
import { GoldmannTonometry } from '../model/manual'

export const WARNING_NOTE_SUFFIX = 'WarningNote'
export const COMMON_ALERT_THRESHOLD = 0.01
export const getInputAlertWarning = (
	alertThresholds?: ClinicalValueAlertThresholds,
	value?: string | number | null,
) => {
	if (!alertThresholds || value == null || value === '') return false

	const numValue = Number(value)

	// Max Yellow warning: valore > maxYellow AND (maxRed non esiste OR valore < maxRed)
	if (
		alertThresholds.maxYellow !== undefined &&
		numValue > alertThresholds.maxYellow
	) {
		if (
			alertThresholds.maxRed === undefined ||
			numValue < alertThresholds.maxRed
		) {
			return true
		}
	}

	// Min Yellow warning: valore < minYellow AND (minRed non esiste OR valore > minRed)
	if (
		alertThresholds.minYellow !== undefined &&
		numValue < alertThresholds.minYellow
	) {
		if (
			alertThresholds.minRed === undefined ||
			numValue > alertThresholds.minRed
		) {
			return true
		}
	}

	return false
}

export const getInputAlertError = (
	alertThresholds?: ClinicalValueAlertThresholds,
	value?: string | number | null,
) => {
	if (!alertThresholds || value == null || value === '') return false

	const numValue = Number(value)

	return (
		(alertThresholds.maxRed !== undefined &&
			numValue >= alertThresholds.maxRed) ||
		(alertThresholds.minRed !== undefined && numValue <= alertThresholds.minRed)
	)
}

export const getInputAlertConfigKeyFromFieldName = (
	fieldName?: string,
): 'sph' | 'cyl' | 'axis' | 'iop' | null => {
	if (!fieldName) return null

	const lowerFieldName = fieldName.toLowerCase()

	if (
		lowerFieldName.includes('sphere') ||
		lowerFieldName.includes('sph') ||
		lowerFieldName === 'sph'
	) {
		return 'sph'
	}
	if (
		lowerFieldName.includes('cylinder') ||
		lowerFieldName.includes('cyl') ||
		lowerFieldName === 'cyl'
	) {
		return 'cyl'
	}
	if (lowerFieldName.includes('axis')) {
		return 'axis'
	}
	if (lowerFieldName.includes('iop')) {
		return 'iop'
	}

	return null
}

export const getInputAlertMinMax = (
	inputAlertConfig: SystemInputAlert | null | undefined,
	field: 'sph' | 'cyl' | 'axis' | 'iop',
	minDefault: number,
	maxDefault: number,
): { min: number; max: number } => {
	const fieldConfig = inputAlertConfig?.[field]

	const minValuePresent = fieldConfig?.minRed || fieldConfig?.minRed === 0
	const maxValuePreset = fieldConfig?.maxRed || fieldConfig?.maxRed === 0

	return {
		min: minValuePresent
			? fieldConfig.minRed! + COMMON_ALERT_THRESHOLD
			: minDefault,
		max: maxValuePreset
			? fieldConfig.maxRed! - COMMON_ALERT_THRESHOLD
			: maxDefault,
	}
}

const collectWarningNotes = (
	eyeData: Record<string, string>,
	labelPrefix: string,
): string[] =>
	Object.keys(eyeData)
		.filter(key => key.endsWith(WARNING_NOTE_SUFFIX) && eyeData[key])
		.map(key => {
			const fieldName = key.replace(WARNING_NOTE_SUFFIX, '')
			const displayName = fieldName.toLowerCase().startsWith('iop')
				? 'IOP' + fieldName.slice(3)
				: fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
			return `<b>${labelPrefix} - ${displayName}</b>: ${eyeData[key]}`
		})

export const getContactLensesWarningNotes = (
	values: ContactLenseFormValues,
	tabIndex: number,
	eye: 'OD' | 'OS',
	label: string,
): string[] => {
	if (!values?.[tabIndex]?.measure?.[eye]) {
		return []
	}
	const measureData = values[tabIndex].measure[eye] as Record<string, string>

	return collectWarningNotes(measureData, `${label} #${tabIndex + 1} ${eye}`)
}

export const getAllContactLensesWarningNotes = (
	values: ContactLenseFormValues,
	label: string,
): string[] =>
	values.flatMap((_, i) => [
		...getContactLensesWarningNotes(values, i, 'OD', label),
		...getContactLensesWarningNotes(values, i, 'OS', label),
	])

export const getContactLensHistoryWarningNotes = (
	title: string,
	values: ContactLensesHistory[],
	tabIndex: number,
	eye: 'OD' | 'OS',
) => {
	if (!values?.[tabIndex]?.measure?.[eye]) {
		return []
	}
	const measureData = values[tabIndex].measure[eye] as Record<string, string>

	return collectWarningNotes(measureData, `${title} #${tabIndex + 1} ${eye}`)
}

export const getPrescriptionSectionCardWarningNotes = (
	title: string,
	values: LensDataWithAccuracy,
	eye: 'OD' | 'OS',
) => {
	if (!values) {
		return []
	}

	const measureData = values[eye] as unknown as Record<string, string>

	return collectWarningNotes(measureData, `${title} ${eye}`)
}

export const getPrescriptionContactLensesWarningNotes = (
	title: string,
	values: PrescriptionContactLensesSingleEye,
	eye: 'OD' | 'OS',
) => {
	if (!values.Measure) {
		return []
	}

	const measureData = values.Measure as Record<string, string>

	return collectWarningNotes(measureData, `${title} ${eye}`)
}

export const getTonometryWarningNotes = (
	goldmanTonometry: GoldmannTonometry[] | undefined,
	label: string,
): string[] => {
	if (!goldmanTonometry) return []

	return goldmanTonometry.flatMap((measurement, index) =>
		(['OD', 'OS'] as const).flatMap(eye =>
			collectWarningNotes(
				(measurement?.[eye] || {}) as Record<string, string>,
				`${label} ${index + 1} ${eye}`,
			),
		),
	)
}

export const getNctWarningNotes = (
	nctValues: Record<'OD' | 'OS', Record<string, string>> | undefined,
): string[] => {
	if (!nctValues) return []

	return (['OD', 'OS'] as const).flatMap(eye =>
		nctValues[eye] ? collectWarningNotes(nctValues[eye], eye) : [],
	)
}

export const getRefractionWarningNotes = (data: PhoropterData): string[] => {
	const warnings: string[] = []

	const instruments = [
		{ key: 'subjectiveRefraction', label: 'Subjective Refraction' },
		{ key: 'cycloplegicRefraction', label: 'Cycloplegic Refraction' },
		{ key: 'finalRefraction', label: 'Final Refraction' },
	] as const

	const warningFields = [
		{ key: 'sphereWarningNote', displayName: 'Sphere' },
		{ key: 'cylWarningNote', displayName: 'Cylinder' },
		{ key: 'axisWarningNote', displayName: 'Axis' },
	] as const

	const conditions = ['day', 'night'] as const
	const variants = ['default', 'other'] as const
	const eyes = ['OD', 'OS'] as const

	instruments.forEach(({ key: instrumentKey, label: instrument }) => {
		const instrumentData = data[instrumentKey]
		if (!instrumentData) return

		conditions.forEach(condition => {
			const conditionData = instrumentData[condition]
			if (!conditionData) return

			variants.forEach(variant => {
				const variantData = conditionData[variant]
				if (!variantData) return

				eyes.forEach(eye => {
					const measureData = variantData.eyes[eye]
					if (!measureData) return

					warningFields.forEach(({ key, displayName }) => {
						const noteValue = measureData[key]

						if (noteValue && noteValue.trim() !== '') {
							warnings.push(
								`<b>${instrument}  ${eye} - ${displayName}</b>: ${noteValue}`,
							)
						}
					})
				})
			})
		})
	})

	return warnings
}

export const getRetinoscopyWarningNotes = (data: RetinoscopyData): string[] => {
	const warnings: string[] = []

	const conditions = [
		{ key: 'dry', label: 'Retinoscopy Dry' },
		{ key: 'wet', label: 'Retinoscopy Wet' },
	] as const

	const warningFields = [
		{ key: 'sphereWarningNote', displayName: 'Sphere' },
		{ key: 'cylinderWarningNote', displayName: 'Cylinder' },
		{ key: 'axisWarningNote', displayName: 'Axis' },
	] as const

	const eyes = ['OD', 'OS'] as const

	conditions.forEach(({ key: conditionKey, label: instrument }) => {
		const conditionData = data[conditionKey]
		if (!conditionData) return

		eyes.forEach(eye => {
			const measureData = conditionData[eye]
			if (!measureData) return

			warningFields.forEach(({ key, displayName }) => {
				const noteValue = measureData[key]

				if (noteValue && noteValue.trim() !== '') {
					warnings.push(
						`<b>${instrument} ${eye} - ${displayName}</b>: ${noteValue}`,
					)
				}
			})
		})
	})

	return warnings
}

export const getLensometryWarningNotes = (
	data: FieldsData[Path][] | null | undefined,
	lnValidIndex?: number[],
	hrValidIndex?: number[]
): string[] => {
	const warnings: string[] = []

	const entityLabels: Record<string, string> = {
		lensometry: 'Lensometry',
		historicalRx: 'Historical Rx',
	}

	const fieldLabels: Record<string, string> = {
		sphereWarningNote: 'Sphere',
		sphWarningNote: 'Sphere',
		cylinderWarningNote: 'Cylinder',
		cylWarningNote: 'Cylinder',
		axisWarningNote: 'Axis',
	}

	data?.forEach(item => {
		if (!item.value || item.value.toString().trim() === '') return

		const parts = item.path.split('.')

		if (parts.length !== 5) return

		const [entityKey, dayOrNight, accuracy, eye, fieldKey] = parts

		const entityName = entityLabels[entityKey] || entityKey
		let index = item.index
		if (entityKey === 'lensometry' && lnValidIndex?.length) {
			index = lnValidIndex.findIndex(v => v === item.index)
		} else if (entityKey === 'historicalRx' && hrValidIndex?.length) {
			index = hrValidIndex.findIndex(v => v === item.index)
		}

		const displayName =
			fieldLabels[fieldKey] || fieldKey.replace('WarningNote', '')

		warnings.push(
			`<b>${entityName} #${index! + 1
			} ${eye.toUpperCase()} - ${displayName}</b>: ${item.value}`,
		)
	})

	return warnings
}

export const getTonometerWarningNotes = (
	data: TonometerData,
	label: string,
): string[] => {
	const warnings: string[] = []
	const instrument = label
	const eyes = ['OD', 'OS'] as const

	// Mapeo de las propiedades de warning con sus display names
	const warningFields = [
		{ key: 'iop1WarningNote', displayName: 'IOP 1' },
		{ key: 'iop2WarningNote', displayName: 'IOP 2' },
		{ key: 'iopcWarningNote', displayName: 'IOPc' },
	] as const

	eyes.forEach(eye => {
		const measureData = data[eye]
		if (!measureData) return

		warningFields.forEach(({ key, displayName }) => {
			const noteValue = measureData[key]

			// Verificamos que la nota exista y no esté vacía
			if (noteValue && noteValue.trim() !== '') {
				warnings.push(
					`<b>${instrument} ${eye} - ${displayName}</b>: ${noteValue}`,
				)
			}
		})
	})

	return warnings
}

export const getAutorefractionWarningNotes = (
	data: AutorefractionData,
	label: string,
): string[] => {
	const warnings: string[] = []
	const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

	const conditions = ['day', 'night'] as const
	const variants = ['default', 'other'] as const
	const eyes = ['OD', 'OS'] as const

	const warningFields = [
		{ key: 'sphereWarningNote', displayName: 'Sphere' },
		{ key: 'cylWarningNote', displayName: 'Cylinder' },
		{ key: 'axisWarningNote', displayName: 'Axis' },
	] as const

	conditions.forEach(condition => {
		const conditionData = data[condition]
		if (!conditionData) return

		variants.forEach(variant => {
			const variantData = conditionData[variant]
			if (!variantData) return

			const suffix = [
				condition !== 'day' ? capitalize(condition) : '',
				variant !== 'default' ? capitalize(variant) : '',
			]
				.filter(Boolean)
				.join(' ')
			const instrument = suffix ? `${label} ${suffix}` : label

			eyes.forEach(eye => {
				const measureData = variantData.eyes[eye]
				if (!measureData) return

				warningFields.forEach(({ key, displayName }) => {
					const noteValue = measureData[key]

					if (noteValue && noteValue.trim() !== '') {
						warnings.push(
							`<b>${instrument} ${eye} - ${displayName}</b>: ${noteValue}`,
						)
					}
				})
			})
		})
	})

	return warnings
}
